import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Autocomplete,
  CircularProgress,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import { showErrorSnackbar, showSuccessSnackbar } from '../../utils/Snackbar';
import { CommHandler } from '../../utils';
import { config } from '../../config';
import { getAuthHeader } from '../../utils/utils';
import { validateAndSanitizeNameWithLimitedSpecialCharacters } from '../../utils/inputSanitizer';

// Interface definitions
interface Category {
  id: string | number;
  name: string; // Name of the Category
}

interface DesignationGroup {
  id: string | number;
  name: string;
  level: number;
}

interface Subcategory {
  id: string | number;
  name: string;
  artifact_category_id?: string | number;
  category_id?: string | number;
}

interface Section {
  id: string | number;
  sectionName: string;
}

interface Question {
  id: string | number;
  name?: string;
  question: string;
  rating?: boolean;
  comment?: boolean;
}

interface SectionWithQuestions {
  sectionId: string | null;
  sectionName?: string;
  questions: Question[];
  questionOption: 'rating' | 'comment' | 'both';
}

interface FormData {
  name: string;
  category: string | null; // Selected BU id
  subcategory: string | null; // Selected Subcategory id (optional)
  designationGroup: string | null; // Selected Designation Group id
  sectionTitle?: string; // New optional field
  questionTitle: string; // Question title as text input
  ratingTitle: string;
  commentTitle: string;
  areaOfImprovement: boolean;
  feedback: boolean;
  // Updated fields for multiple sections
  sectionsWithQuestions: SectionWithQuestions[];
  approver: Array<string | null>;
}

// Helper tooltips
const infoMessages = {
  formName: 'Enter a name for the form',
  category: 'Select a category',
  designationGroup: 'Select a designation group',
  questionTitle: 'Enter a question title for this form',
};

const AddForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Check if editing (data passed via navigation state)
  const editData = location.state?.data;

  // State for form data, prefill if editing (handle nested objects and ids)
  const [formData, setFormData] = useState<FormData>({
    name: editData?.form?.name || '',
    category: editData?.form?.category?.id || editData?.form?.category_id || null,
    subcategory: editData?.form?.sub_category?.id || editData?.form?.sub_category_id || null,
    designationGroup: editData?.form?.designationGroup?.id || editData?.form?.designation_group_id || null,
    sectionTitle: editData?.form?.section_title || '',
    questionTitle: editData?.form?.question_title || '',
    ratingTitle: editData?.form?.rating_title || '',
    commentTitle: editData?.form?.comment_title || '',
    areaOfImprovement: !!editData?.form?.area_of_improvement,
    feedback: editData?.form?.feedback || '',
    // Updated fields for multiple sections
    sectionsWithQuestions: editData?.form?.sections_with_questions?.map((section: any) => ({
      sectionId: section.section_id || null,
      sectionName: '',
      questions: [],
      questionOption: section.includeRating ? 'rating' : 'comment',
    })) || [
      {
        sectionId: null,
        sectionName: '',
        questions: [],
        questionOption: 'rating',
      },
    ],
    approver: editData?.form?.approver?.map((id: any) => id.toString()) || editData?.form?.approverId?.map((id: any) => id.toString()) || null,
  });
  // State for users dropdown
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);

  // State for dropdown options
  const [categories, setCategories] = useState<Category[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<Subcategory[]>([]);
  const [designationGroups, setDesignationGroups] = useState<DesignationGroup[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);

  // Loading states
  const [buLoading, setBuLoading] = useState<boolean>(false);
  const [subcategoryLoading, setSubcategoryLoading] = useState<boolean>(false);
  const [designationsLoading, setDesignationsLoading] = useState<boolean>(false);
  const [sectionsLoading, setSectionsLoading] = useState<boolean>(false);
  const [questionsLoading, setQuestionsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Dialog state
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

  // Error states for validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Computed property to filter subcategories based on selected category
  const filteredSubcategories = formData.category ? allSubcategories.filter((sub) => String(sub.artifact_category_id) === String(formData.category)) : [];

  // Helper function to check if Company category is selected
  const isCompanyCategorySelected = useCallback(() => {
    const selectedCategory = categories.find((cat) => String(cat.id) === String(formData.category));
    return selectedCategory && selectedCategory.name?.toLowerCase() === 'company';
  }, [categories, formData.category]);

  // Fetch users for approver dropdown
  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/users`,
        method: 'GET',
        headers: getAuthHeader,
      });
      if (response?.status === 200 && response?.data) {
        setUsers(response.data || []);
      } else {
        setUsers([]);
      }
    } catch (error) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Update data mapping to use response.data instead of response.data.data
  const fetchCategories = async () => {
    try {
      setBuLoading(true);
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/artifact/category`,
        method: 'GET',
        headers: getAuthHeader,
      });

      if (response?.status === 200 && response?.data) {
        const categoryData = response?.data || [];
        const mappedCategories = Array.isArray(categoryData)
          ? categoryData.map((item) => ({
              id: item?.id || '',
              name: item?.name || 'Unnamed Category',
            }))
          : [];
        setCategories(mappedCategories);
      } else {
        showErrorSnackbar('Failed to load categories');
      }
    } catch (error) {
      showErrorSnackbar('Error loading categories');
    } finally {
      setBuLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      setSubcategoryLoading(true);
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/artifact/subcategory`,
        method: 'GET',
        headers: getAuthHeader,
      });

      if (response?.status === 200 && response?.data) {
        const subcategoryData = response?.data || [];
        const mappedSubcategories = Array.isArray(subcategoryData)
          ? subcategoryData.map((item) => ({
              id: item?.id || '',
              name: item?.name || 'Unnamed Subcategory',
              artifact_category_id: item?.artifact_category_id,
              category_id: item?.category_id,
            }))
          : [];
        setAllSubcategories(mappedSubcategories);
      } else {
        setAllSubcategories([]);
        // Don't show error for subcategories as they might not exist for all categories
      }
    } catch (error) {
      setAllSubcategories([]);
      // Silent fail for subcategories
    } finally {
      setSubcategoryLoading(false);
    }
  };

  const fetchDesignationGroups = async () => {
    try {
      setDesignationsLoading(true);
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/evaluation/designation-group`,
        method: 'GET',
        headers: getAuthHeader,
      });

      if (response?.status === 200 && response?.data) {
        const designationGroupData = response?.data || [];
        const mappedDesignationGroups = Array.isArray(designationGroupData)
          ? designationGroupData.map((item) => ({
              id: item?.id || '',
              name: item?.name || 'Unnamed Designation Group',
              level: item?.level || 1,
            }))
          : [];
        setDesignationGroups(mappedDesignationGroups);
      } else {
        showErrorSnackbar('Failed to load designation groups');
      }
    } catch (error) {
      showErrorSnackbar('Error loading designation groups');
    } finally {
      setDesignationsLoading(false);
    }
  };

  // Update logic to handle both response.data.data and response.data
  const fetchSections = async () => {
    try {
      setSectionsLoading(true);
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/section`,
        method: 'GET',
        headers: getAuthHeader,
      });

      if (response?.status === 200 && response?.data) {
        const sectionData = response?.data?.data || response?.data || [];
        const mappedSections = Array.isArray(sectionData)
          ? sectionData.map((item) => ({
              id: item?.id || '',
              sectionName: item?.sectionName || item?.name || 'Unnamed Section',
            }))
          : [];
        setSections(mappedSections);
      } else {
        showErrorSnackbar('Failed to load sections');
      }
    } catch (error) {
      showErrorSnackbar('Error loading sections');
    } finally {
      setSectionsLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      setQuestionsLoading(true);
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/questions`,
        method: 'GET',
        headers: getAuthHeader,
      });

      if (response?.status === 200 && response?.data) {
        const questionData = response?.data || [];
        const mappedQuestions = Array.isArray(questionData)
          ? questionData.map((item) => ({
              id: item?.id || '',
              name: item?.name || item?.title || '',
              question: item?.question || 'Unnamed Question',
              rating: item?.rating || false,
              comment: item?.comment || false,
            }))
          : [];
        setQuestions(mappedQuestions);
      } else {
        showErrorSnackbar('Failed to load questions');
      }
    } catch (error) {
      showErrorSnackbar('Error loading questions');
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Fetch users for approver dropdown
  useEffect(() => {
    if (isCompanyCategorySelected()) {
      fetchUsers();
    }
  }, [formData.category, categories, isCompanyCategorySelected, fetchUsers]);

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
    fetchDesignationGroups();
    fetchSections();
    fetchQuestions();
    fetchSubcategories();
  }, []);

  // Handle subcategory initialization for edit mode
  useEffect(() => {
    // Only run in edit mode when subcategories are loaded and we have a sub_category_id
    if (editData?.form?.sub_category_id && allSubcategories.length > 0 && formData.category) {
      const subcategoryId = editData.form.sub_category_id;

      // Find the subcategory that matches the ID and belongs to the selected category
      const matchingSubcategory = allSubcategories.find((subcat) => String(subcat.id) === String(subcategoryId) && String(subcat.artifact_category_id) === String(formData.category));

      // Only update if we found a matching subcategory and it's different from current value
      if (matchingSubcategory && String(formData.subcategory) !== String(subcategoryId)) {
        setFormData((prev) => ({
          ...prev,
          subcategory: String(subcategoryId),
        }));
      }
    }
  }, [editData?.form?.sub_category_id, allSubcategories, formData.category, formData.subcategory]);

  // Auto-fill sections and questions when editing a form
  useEffect(() => {
    // Only run in edit mode when sections and questions are loaded
    if (editData?.form?.sections_with_questions && sections.length > 0 && questions.length > 0 && formData.sectionsWithQuestions.length > 0 && formData.sectionsWithQuestions[0].questions.length === 0) {
      const sectionsWithQuestions = editData.form.sections_with_questions.map((sectionData: any) => {
        // Find section by ID
        const section = sections.find((s) => String(s.id) === String(sectionData.section_id));

        // Find questions by IDs (handle both string and number IDs)
        const sectionQuestions = questions.filter((q) => sectionData.question_ids.includes(Number(q.id)) || sectionData.question_ids.includes(String(q.id)));

        // // Determine if this section should include rating/comment based on the questions
        // const hasRatingQuestions = sectionQuestions.some(q => q.rating);
        // const hasCommentQuestions = sectionQuestions.some(q => q.comment);

        return {
          sectionId: String(sectionData.section_id),
          sectionName: section?.sectionName || '',
          questions: sectionQuestions,
          questionOption: sectionData.includeRating ? 'rating' : 'comment',
        };
      });

      setFormData((prev) => ({
        ...prev,
        sectionsWithQuestions,
      }));
    }
  }, [editData?.form?.sections_with_questions, sections, questions, formData.sectionsWithQuestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let processedValue = value;
    let validationError = '';

    // Apply validation for specific fields
    const fieldsToValidate = ['name', 'sectionTitle', 'questionTitle', 'ratingTitle', 'commentTitle'];
    if (fieldsToValidate.includes(name)) {
      const { isValid, sanitizedValue, error } = validateAndSanitizeNameWithLimitedSpecialCharacters(value, 100);
      processedValue = sanitizedValue;
      validationError = isValid ? '' : error || `${name} should not be more than 100 characters and must use allowed characters`;
    }

    setFormData({
      ...formData,
      [name]: processedValue,
    });

    // Set or clear validation error
    setErrors({
      ...errors,
      [name]: validationError,
    });
  };

  // Handle dropdown changes
  const handleDropdownChange = (name: string, value: any) => {
    setFormData((prevFormData) => {
      // If approver field → append selected IDs (multi-select)
      if (name === 'approver') {
        return {
          ...prevFormData,
          approver: value, // value is already array of IDs from Autocomplete
        };
      }

      // If category changed → reset dependent fields
      if (name === 'category') {
        return {
          ...prevFormData,
          category: value,
          subcategory: null,
          approver: [], // clear approvers
        };
      }

      // Default case
      return {
        ...prevFormData,
        [name]: value,
      };
    });

    // Clear validation error when user selects
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  // Handle adding a new section
  const addNewSection = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      sectionsWithQuestions: [
        ...prevFormData.sectionsWithQuestions,
        {
          sectionId: null,
          sectionName: '',
          questions: [],
          questionOption: 'rating',
        },
      ],
    }));
  };

  // Handle removing a section
  const removeSection = (index: number) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      sectionsWithQuestions: prevFormData.sectionsWithQuestions.filter((_, i) => i !== index),
    }));
  };

  // Handle section selection for a specific section
  const handleSectionChange = (index: number, sectionId: string | null, sectionName?: string) => {
    setFormData((prevFormData) => {
      const updatedSections = [...prevFormData.sectionsWithQuestions];
      updatedSections[index] = {
        ...updatedSections[index],
        sectionId,
        sectionName: sectionName || '',
        questions: [], // Reset questions when section changes
      };
      return {
        ...prevFormData,
        sectionsWithQuestions: updatedSections,
      };
    });
  };

  // Handle questions selection for a specific section
  const handleSectionQuestionsChange = (index: number, newQuestions: Question[]) => {
    setFormData((prevFormData) => {
      const updatedSections = [...prevFormData.sectionsWithQuestions];
      updatedSections[index] = {
        ...updatedSections[index],
        questions: newQuestions,
      };
      return {
        ...prevFormData,
        sectionsWithQuestions: updatedSections,
      };
    });
  };

  // Handle section-specific radio changes for question options
  const handleSectionRadioChange = (index: number, value: 'rating' | 'comment' | 'both') => {
    setFormData((prevFormData) => {
      const updatedSections = [...prevFormData.sectionsWithQuestions];
      const currentSection = updatedSections[index];

      // Update the radio value
      const updatedSection = {
        ...currentSection,
        questionOption: value,
      };

      // Filter questions based on the selected option
      if (value === 'rating') {
        updatedSection.questions = (currentSection.questions || []).filter((question) => question.rating);
      } else if (value === 'comment') {
        updatedSection.questions = (currentSection.questions || []).filter((question) => question.comment);
      }

      updatedSections[index] = updatedSection;

      return {
        ...prevFormData,
        sectionsWithQuestions: updatedSections,
      };
    });
  };

  // Create a function to get filtered questions for a specific section
  const getFilteredQuestionsForSection = (sectionIndex: number) => {
    const section = formData.sectionsWithQuestions[sectionIndex];
    if (!section) return [];

    return questions.filter((question) => {
      // Apply section-specific filters based on radio selection
      if (section.questionOption === 'rating' && !question.rating) {
        return false;
      }
      if (section.questionOption === 'comment' && !question.comment) {
        return false;
      }
      return true;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validate form name with sanitization
    if (!formData.name.trim()) {
      newErrors.name = 'Form name is required';
    } else {
      const { isValid, error } = validateAndSanitizeNameWithLimitedSpecialCharacters(formData.name, 100);
      if (!isValid) {
        newErrors.name = error || 'Form name should not be more than 100 characters and must use allowed characters';
      }
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.designationGroup) {
      newErrors.designationGroup = 'Designation group is required';
    }

    // Validate approver for Company category
    if (isCompanyCategorySelected() && !formData.approver) {
      newErrors.approver = 'Approver is required for Company category';
    }

    // Validate sectionTitle, questionTitle, ratingTitle, and commentTitle from formData
    if (formData.sectionTitle) {
      const { isValid, error } = validateAndSanitizeNameWithLimitedSpecialCharacters(formData.sectionTitle, 100);
      if (!isValid) {
        newErrors.sectionTitle = error || 'Section title should not be more than 100 characters and must use allowed characters';
      }
    }
    if (formData.questionTitle) {
      const { isValid, error } = validateAndSanitizeNameWithLimitedSpecialCharacters(formData.questionTitle, 100);
      if (!isValid) {
        newErrors.questionTitle = error || 'Question title should not be more than 100 characters and must use allowed characters';
      }
    }
    if (formData.ratingTitle) {
      const { isValid, error } = validateAndSanitizeNameWithLimitedSpecialCharacters(formData.ratingTitle, 100);
      if (!isValid) {
        newErrors.ratingTitle = error || 'Rating title should not be more than 100 characters and must use allowed characters';
      }
    }
    if (formData.commentTitle) {
      const { isValid, error } = validateAndSanitizeNameWithLimitedSpecialCharacters(formData.commentTitle, 100);
      if (!isValid) {
        newErrors.commentTitle = error || 'Comment title should not be more than 100 characters and must use allowed characters';
      }
    }

    // Validate sections and questions
    const validSections = formData.sectionsWithQuestions.filter((section) => section.sectionId && section.questions?.length > 0);

    if (validSections.length === 0) {
      newErrors.sections = 'At least one section with questions is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isEditMode = Boolean(editData && editData?.form?.id);

  // Fetch users on component mount if editing and category is already Company
  useEffect(() => {
    if (isEditMode && categories.length > 0 && isCompanyCategorySelected() && users.length === 0) {
      fetchUsers();
    }
  }, [categories, isEditMode, users.length, formData.category, isCompanyCategorySelected, fetchUsers]);

  const hasDuplicateByKey = (array: any[], key: any[]) => {
    const seen = new Set();
    return array.filter((item) => {
      const value = key.map((k) => item[k]).join('|');
      if (seen.has(value)) {
        return true;
      }
      seen.add(value);
      return false;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showErrorSnackbar('Please fill all required fields');
      return;
    }

    const sectionsWithQuestions = [...formData.sectionsWithQuestions];
    const isDuplicate = hasDuplicateByKey(sectionsWithQuestions, ['sectionName', 'questions']);

    if (isDuplicate.length > 0) {
      showErrorSnackbar('Duplicate questions and sections found in Question Builder Section');
      return;
    }

    try {
      setIsSubmitting(true);
      // Prepare data for submission
      const formPayload = {
        name: formData.name ? formData.name.trim() : '',
        designation_group_id: formData.designationGroup || null,
        category_id: formData.category || null,
        sub_category_id: formData.subcategory || null,
        section_title: formData.sectionTitle ? formData.sectionTitle.trim() : '',
        question_title: formData.questionTitle ? formData.questionTitle.trim() : '',
        rating_title: formData.ratingTitle ? formData.ratingTitle.trim() : '',
        comment_title: formData.commentTitle ? formData.commentTitle.trim() : '',
        area_of_improvement: !!formData.areaOfImprovement,
        feedback: !!formData.feedback,
        approver: isCompanyCategorySelected() ? formData.approver : null,
        // Updated fields for multiple sections
        sections_with_questions: formData.sectionsWithQuestions
          .filter((section) => section.sectionId && section.questions?.length > 0)
          .map((section) => ({
            section_id: section.sectionId,
            question_ids: section.questions?.map((q) => q.id) || [],
            includeRating: section.questionOption === 'rating' || section.questionOption === 'both',
            includeComment: section.questionOption === 'comment' || section.questionOption === 'both',
          })),
      };
      let response;
      if (isEditMode) {
        response = await CommHandler.request({
          url: `${config.apiBaseUrl}/evaluation/form/${editData?.form?.id}`,
          method: 'PUT',
          headers: getAuthHeader,
          data: formPayload,
        });
      } else {
        response = await CommHandler.request({
          url: `${config.apiBaseUrl}/evaluation/form`,
          method: 'POST',
          headers: getAuthHeader,
          data: formPayload,
        });
      }
      if (response.status === 200 || response.status === 201) {
        showSuccessSnackbar(isEditMode ? 'Form updated successfully' : 'Form created successfully');
        navigate('/evaluation', { state: { tabIndex: 2 } });
      } else {
        showErrorSnackbar(response.data?.message || (isEditMode ? 'Failed to update form' : 'Failed to create form'));
      }
    } catch (error) {
      showErrorSnackbar(isEditMode ? 'Error updating form. Please try again.' : 'Error creating form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Show confirmation dialog instead of navigating directly
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleDiscard = () => {
    // Close the dialog and navigate away
    setOpenConfirmDialog(false);
    navigate('/evaluation', { state: { tabIndex: 2 } });
  };

  const saveButtonText = isEditMode ? 'Update' : 'Submit';

  return (
    <Box
      sx={{
        p: '24px',
        bgcolor: 'white',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
        m: '24px',
        width: 'calc(100% - 48px)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={handleCancel}
          sx={{
            mr: 1,
            color: '#1976d2',
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="600">
          {isEditMode ? 'Edit Form' : 'Create Form'}
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Form Name<span style={{ color: 'red' }}>*</span>
              </Typography>
              <Tooltip title={infoMessages.formName}>
                <IconButton sx={{ ml: 0.5, p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#6b778c' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              placeholder="Form name"
              variant="outlined"
              error={!!errors.name}
              helperText={errors.name}
              FormHelperTextProps={{
                sx: {
                  marginLeft: '0px',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Category<span style={{ color: 'red' }}>*</span>
              </Typography>
              <Tooltip title={infoMessages.category}>
                <IconButton sx={{ ml: 0.5, p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#6b778c' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.name || ''}
              loading={buLoading}
              value={categories.find((cat) => String(cat.id) === String(formData.category)) || null}
              onChange={(_, newValue) => handleDropdownChange('category', newValue ? newValue.id : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select category"
                  variant="outlined"
                  error={!!errors.category}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {buLoading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {errors.category && <FormHelperText error>{errors.category}</FormHelperText>}
            {/* Approver dropdown for Company category - moved below Category */}
            {isCompanyCategorySelected() && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    Select Approver<span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Tooltip title="Select an approver for Company category">
                    <IconButton sx={{ ml: 0.5, p: 0 }}>
                      <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#6b778c' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormControl fullWidth variant="outlined" error={!!errors.approver}>
                  <Autocomplete
                    multiple
                    options={users}
                    value={users.filter((u) => (formData.approver || []).includes(String(u.id || u.profile?.user_id || u.employeeID || u.email)))}
                    onChange={(event, newValues) => {
                      const selectedIds = newValues.map((u) => u?.id?.toString() || u?.profile?.user_id?.toString() || u?.employeeID?.toString() || u.email);

                      handleDropdownChange('approver', selectedIds);

                      setErrors((prev) => ({ ...prev, approver: '' }));
                    }}
                    getOptionLabel={(option) => option.fullName || option.email || 'Unknown User'}
                    isOptionEqualToValue={(option, value) => {
                      const optionId = String(option.id || option.profile?.user_id || option.employeeID || option.email);
                      const valueId = String(value.id || value.profile?.user_id || value.employeeID || value.email);
                      return optionId === valueId;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select approver"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id || option.profile?.user_id || option.employeeID || option.email}>
                        <Box>
                          <Typography variant="body1">{option.fullName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.email} • {option.employeeID}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    loading={usersLoading}
                    disabled={usersLoading}
                    noOptionsText="No users found"
                    filterOptions={(options, { inputValue }) => {
                      return options.filter(
                        (option) => option.fullName?.toLowerCase().includes(inputValue.toLowerCase()) || option.email?.toLowerCase().includes(inputValue.toLowerCase()) || option.employeeID?.toLowerCase().includes(inputValue.toLowerCase()),
                      );
                    }}
                  />
                  {!!errors.approver && <FormHelperText error>{errors.approver}</FormHelperText>}
                </FormControl>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Subcategory
              </Typography>
              <Tooltip title="Select a subcategory">
                <IconButton sx={{ ml: 0.5, p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#6b778c' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Autocomplete
              options={filteredSubcategories}
              getOptionLabel={(option) => option.name || ''}
              loading={subcategoryLoading}
              disabled={!formData.category || subcategoryLoading}
              value={filteredSubcategories.find((subcat) => String(subcat.id) === String(formData.subcategory)) || null}
              onChange={(_, newValue) => handleDropdownChange('subcategory', newValue ? newValue.id : null)}
              renderInput={(params) => {
                let placeholderText = 'Select category first';
                if (formData.category) {
                  placeholderText = filteredSubcategories.length > 0 ? `Select subcategory (${filteredSubcategories.length} available)` : 'No subcategories available for this category';
                }
                return (
                  <TextField
                    {...params}
                    placeholder={placeholderText}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {subcategoryLoading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Designation Group<span style={{ color: 'red' }}>*</span>
              </Typography>
              <Tooltip title={infoMessages.designationGroup}>
                <IconButton sx={{ ml: 0.5, p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#6b778c' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Autocomplete
              options={designationGroups}
              getOptionLabel={(option) => {
                if (option.name && option.name.trim()) {
                  return `L${option.level}:${option.name}`;
                }
                return `L${option.level}`;
              }}
              loading={designationsLoading}
              value={designationGroups.find((dg) => String(dg.id) === String(formData.designationGroup)) || null}
              onChange={(_, newValue) => handleDropdownChange('designationGroup', newValue ? newValue.id : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select designation group"
                  variant="outlined"
                  error={!!errors.designationGroup}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {designationsLoading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {errors.designationGroup && <FormHelperText error>{errors.designationGroup}</FormHelperText>}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Section Title
              </Typography>
            </Box>
            <TextField name="sectionTitle" value={formData.sectionTitle} onChange={handleInputChange} fullWidth placeholder="Enter section title" variant="outlined" />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Question Title
              </Typography>
            </Box>
            <TextField name="questionTitle" value={formData.questionTitle} onChange={handleInputChange} fullWidth placeholder="Enter question title" variant="outlined" />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Rating Title
              </Typography>
            </Box>
            <TextField name="ratingTitle" value={formData.ratingTitle} onChange={handleInputChange} fullWidth placeholder="Enter rating title" variant="outlined" />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="600">
                Comment Title
              </Typography>
            </Box>
            <TextField name="commentTitle" value={formData.commentTitle} onChange={handleInputChange} fullWidth placeholder="Enter comment title" variant="outlined" />
          </Box>

          <Box sx={{ mb: 3, p: 3, display: 'flex', gap: 3, backgroundColor: '#f9f9f9' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.areaOfImprovement}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      areaOfImprovement: e.target.checked,
                    });
                  }}
                  color="primary"
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                />
              }
              label={
                <Typography variant="subtitle1" fontWeight="600">
                  Include Area of Improvement
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.feedback}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      feedback: e.target.checked,
                    });
                  }}
                  color="primary"
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                />
              }
              label={
                <Typography variant="subtitle1" fontWeight="600">
                  Include Feedback
                </Typography>
              }
            />
          </Box>

          <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>{/* ...existing code... */}</Box>

          {/* New Section - Multi-Section Question Builder */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              border: '2px solid #1976d2',
              borderRadius: '8px',
              backgroundColor: '#f8f9ff',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                Question Builder
              </Typography>
              <Button variant="outlined" size="small" onClick={addNewSection} sx={{ minWidth: 'auto' }}>
                + Add Section
              </Button>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Add multiple sections with their associated questions to build your form
            </Typography>

            {/* Sections List */}
            {formData.sectionsWithQuestions.map((sectionData, index) => (
              <Paper
                key={sectionData.sectionId || `section-${index}`}
                elevation={1}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    Section {index + 1}
                  </Typography>
                  {formData.sectionsWithQuestions.length > 1 && (
                    <Button variant="outlined" color="error" size="small" onClick={() => removeSection(index)} sx={{ minWidth: 'auto' }}>
                      Remove
                    </Button>
                  )}
                </Box>

                {/* Section Selection */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="600">
                      Select Section<span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Tooltip title="Please select a section for your questions">
                      <IconButton sx={{ ml: 0.5, p: 0 }}>
                        <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: '#6b778c' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Autocomplete
                    options={sections}
                    getOptionLabel={(option) => option.sectionName || ''}
                    loading={sectionsLoading}
                    value={sections.find((section) => String(section.id) === String(sectionData.sectionId)) || null}
                    onChange={(_, newValue) => handleSectionChange(index, newValue ? String(newValue.id) : null, newValue?.sectionName)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select section"
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {sectionsLoading && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Section-specific Question Options */}
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '1.25rem', mb: 1 }}>
                      Question Options for this Section
                    </FormLabel>
                    <RadioGroup row value={sectionData.questionOption} onChange={(e) => handleSectionRadioChange(index, e.target.value as 'rating' | 'comment' | 'both')}>
                      <FormControlLabel value="rating" control={<Radio size="small" />} label="Rating" />
                      <FormControlLabel value="comment" control={<Radio size="small" />} label="Comment" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Questions Multiselect for this section */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="600">
                      Select Questions<span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Tooltip title="Select multiple questions (use checkboxes)">
                      <IconButton sx={{ ml: 0.5, p: 0 }}>
                        <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: '#6b778c' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Autocomplete
                    multiple
                    options={getFilteredQuestionsForSection(index)}
                    getOptionLabel={(option) => option.name || option.question}
                    value={sectionData.questions || []}
                    onChange={(_, newValue) => handleSectionQuestionsChange(index, newValue)}
                    loading={questionsLoading}
                    disabled={!sectionData.sectionId}
                    renderTags={(value, getTagProps) => value.map((option, tagIndex) => <Chip variant="outlined" label={option.name || option.question} {...getTagProps({ index: tagIndex })} key={option.id} size="small" />)}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} style={{ marginRight: 8 }} size="small" />
                        <Box sx={{ width: '100%' }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{option.name || 'Unnamed Question'}</Typography>
                          {option.question && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              {option.question.length > 60 ? `${option.question.substring(0, 60)}...` : option.question}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            {option.rating && <Chip label="Rating" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.55rem', height: '14px' }} />}
                            {option.comment && <Chip label="Comment" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.55rem', height: '14px' }} />}
                          </Box>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => {
                      let questionPlaceholder = 'Select a section first';
                      if (sectionData.sectionId) {
                        const optionText = sectionData.questionOption === 'both' ? 'rating and comment' : sectionData.questionOption;
                        questionPlaceholder = `Search questions for ${optionText}`;
                      }
                      return (
                        <TextField
                          {...params}
                          placeholder={questionPlaceholder}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {questionsLoading && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      );
                    }}
                  />
                </Box>

                {/* Questions count and filter info for this section */}
                <Box sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1rem' }}>
                    {(() => {
                      const availableQuestions = getFilteredQuestionsForSection(index).length;
                      const selectedQuestions = sectionData.questions?.length || 0;

                      let filterText = '';
                      if (sectionData.questionOption === 'rating') {
                        filterText = 'with rating';
                      } else if (sectionData.questionOption === 'comment') {
                        filterText = 'with comment';
                      }

                      return `${selectedQuestions} selected | ${availableQuestions} questions ${filterText}`;
                    })()}
                  </Typography>
                </Box>
              </Paper>
            ))}

            {/* Filter status message */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1rem' }}>
                {(() => {
                  const totalQuestions = questions.length;
                  const totalSelected = formData.sectionsWithQuestions.reduce((sum, section) => sum + (section.questions?.length || 0), 0);
                  const totalSections = formData.sectionsWithQuestions.length;
                  const completedSections = formData.sectionsWithQuestions.filter((section) => section.sectionId && (section.questions?.length || 0) > 0).length;

                  return `${totalSelected} total questions selected across ${totalSections} section(s) | ${completedSections} section(s) completed | ${totalQuestions} total questions available`;
                })()}
              </Typography>
            </Box>

            {/* Validation error message */}
            {errors.sections && (
              <Box sx={{ mt: 1 }}>
                <FormHelperText error>{errors.sections}</FormHelperText>
              </Box>
            )}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, gap: 2, mt: 4, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                minWidth: 120,
                borderRadius: '24px',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px 0 rgba(24, 144, 255, 0.15)',
                transition: 'background 0.3s',
                '&:hover': {
                  backgroundColor: '#1765ad',
                },
                mb: { xs: 2, sm: 0 },
                color: '#ffffff', // Ensure text color is white
              }}
              disableElevation
              aria-label={isEditMode ? 'Update form' : 'Submit form'}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : saveButtonText}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancel}
              sx={{
                minWidth: 120,
                borderRadius: '24px',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                border: '2px solid #1890ff',
                color: '#1890ff',
                background: '#f0f5ff',
                transition: 'background 0.3s, border 0.3s',
                '&:hover': {
                  backgroundColor: '#e6f7ff',
                  borderColor: '#1765ad',
                  color: '#1765ad',
                },
              }}
              aria-label="Cancel and go back"
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="discard-confirmation-dialog-title"
        aria-describedby="discard-confirmation-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '8px',
            minWidth: '400px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle id="discard-confirmation-dialog-title" sx={{ pb: 1, fontWeight: 600, fontSize: '18px' }}>
          Confirm Discard
        </DialogTitle>
        <DialogContent sx={{ pb: 2, pt: 0 }}>
          <DialogContentText id="discard-confirmation-dialog-description" sx={{ color: '#333' }}>
            Are you sure you want to discard the form?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseConfirmDialog}
            variant="outlined"
            sx={{
              color: '#1976d2',
              borderColor: '#1976d2',
              textTransform: 'none',
              fontSize: '14px',
              minWidth: '80px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDiscard}
            variant="contained"
            sx={{
              bgcolor: '#e53935',
              '&:hover': { bgcolor: '#c62828' },
              textTransform: 'none',
              fontSize: '14px',
              minWidth: '80px',
              boxShadow: 'none',
              color: '#ffffff',
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddForm;
